import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Selector } from 'sonata-react-components';

import { canSaveGroupEdit } from '../../domain/groupRules';
import { useGrouping } from '../../hooks/useGrouping';
import { useGroupingItems } from '../../hooks/useGroupingItems';
import { DeleteGroupPrompt } from '../DeleteGroupPrompt/DeleteGroupPrompt';
import { GroupingModal } from '../GroupingModal/GroupingModal';
import { groupingMessages } from '../groupingMessages';
import { firstOptionValue, toGroupOptions } from '../selectorOptions';
import { EditGroupDetail } from './EditGroupDetail';
import { toEditGroupChange } from './editGroupChange';
import { useGroupEditDraft } from './useGroupEditDraft';

import './editGroupsModal.scss';

import type { GroupChange, GroupFamily, GroupReferences } from '../../domain/grouping.types';
import type { GroupingNotice } from '../groupingNotice.types';

export interface EditGroupsModalProps {
  family: GroupFamily;
  /** Opens directly on this group's detail (e.g. from the group menu of a list row). */
  initialGroupId?: string;
  onApplied: (notice: GroupingNotice) => void;
  onClose: () => void;
}

interface PendingDeletion {
  change: GroupChange;
  references: GroupReferences;
}

/** "Edit Groups": pick an editable group, then rename/recolor/manage members or delete it on "Save changes" (as allowed). */
export function EditGroupsModal({ family, initialGroupId, onApplied, onClose }: Readonly<EditGroupsModalProps>) {
  const { formatMessage } = useIntl();
  const grouping = useGrouping(family);
  const items = useGroupingItems(family);
  const options = toGroupOptions(grouping.editableGroups);
  const [groupId, setGroupId] = useState<string | null>(
    options.some(option => option.value === initialGroupId) ? (initialGroupId ?? null) : null
  );
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
  const draftState = useGroupEditDraft(grouping, groupId);
  const group = grouping.editableGroups.find(({ id }) => id === groupId);
  const { draft, original } = draftState;

  const canSave =
    groupId !== null &&
    draft !== null &&
    original !== null &&
    canSaveGroupEdit({ draft, groupId, groups: grouping.inUseGroups, original });

  const apply = (change: GroupChange) => {
    const { deletedGroups } = grouping.applyGroupChange(change);
    onApplied(
      deletedGroups.length
        ? { groupName: original?.name ?? '', kind: 'deleted' }
        : { groupName: draft?.name.trim() ?? '', kind: 'updated' }
    );
  };

  const handleSave = () => {
    if (!groupId || !draft || !original) {
      return;
    }
    const change = toEditGroupChange(groupId, original, draft);
    const { deletedGroups, references } = grouping.previewGroupChange(change);
    if (deletedGroups.length && references.ads + references.temporalPacing > 0) {
      setPendingDeletion({ change, references });
      return;
    }
    apply(change);
  };

  return (
    <>
      <GroupingModal
        description={formatMessage(groupingMessages.editGroupsDescription)}
        id="editGroupsModal"
        leftButton={
          <div className="groupingModal__footerLeft">
            <ButtonUI
              id="editGroupsModal-cancel"
              onClick={onClose}
              shape="squareRounded"
              text={formatMessage(groupingMessages.cancel)}
              type="secondary"
            />
            {draft && (
              <ButtonUI
                icon="fa-regular fa-trash"
                id="editGroupsModal-deleteGroup"
                onClick={draftState.toggleDeleteGroup}
                shape="link"
                text={formatMessage(draft.deleteGroup ? groupingMessages.keepGroup : groupingMessages.deleteGroup)}
                type="secondary"
              />
            )}
          </div>
        }
        onClose={onClose}
        rightButton={
          <ButtonUI
            disabled={!canSave}
            icon="fa-regular fa-floppy-disk"
            id="editGroupsModal-save"
            onClick={handleSave}
            shape="squareRounded"
            text={formatMessage(groupingMessages.saveChanges)}
            type="primary"
          />
        }
        title={formatMessage(groupingMessages.editGroupsTitle)}
      >
        <Selector
          label={formatMessage(groupingMessages.groups)}
          onChange={selected => setGroupId(firstOptionValue(selected))}
          options={options}
          placeholder={formatMessage(groupingMessages.selectPlaceholder)}
          type="simple"
          value={options.filter(option => option.value === groupId)}
        />
        {draft && group && (
          <EditGroupDetail
            capabilities={group.capabilities}
            draftState={{ ...draftState, draft }}
            family={family}
            items={items}
            nameError={group.capabilities.canRename ? grouping.validateName(draft.name, group.id) : null}
          />
        )}
      </GroupingModal>
      {pendingDeletion && (
        <DeleteGroupPrompt
          groupName={original?.name ?? ''}
          onCancel={() => setPendingDeletion(null)}
          onConfirm={() => apply(pendingDeletion.change)}
          references={pendingDeletion.references}
        />
      )}
    </>
  );
}
